import React, { useState, useEffect, useRef, useContext } from 'react';
import { styled, createGlobalStyle, css } from 'styled-components';
import { useTranslations } from 'use-intl';
import { useLanguage } from 'i18n';
import { EditorState, ContentState } from 'draft-js';
import { motion } from 'motion/react';
import { useWindowSize } from 'hooks';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { Button } from 'components';
import { FirebaseContext } from 'context';
import ToolbarDropdown from '../../ToolbarDropdown';

// eslint-disable-next-line react/jsx-no-useless-fragment, react/function-component-definition
let Editor = () => <></>;

const wrapperStyle = {
  height: '7.5rem'
};

const toolbarStyle = {
  height: '2rem',
  padding: '0.313rem 0.25rem'
};

const editorStyle = (editorIsFocused) => ({
  background: editorIsFocused ? 'rgba(226, 228, 234, 0.40)' : '#fff',
  color: 'var(--brown)',
  border: editorIsFocused
    ? `0.063rem solid var(--blue)`
    : `0.063rem solid var(--light-grey)`,
  borderRadius: '18px',
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.875rem',
  fontWeight: 400,
  height: '100%',
  minHeight: '120px',
  outline: 'none',
  overflow: 'auto',
  padding: '0rem 0.625rem 2.125rem',
  wordWrap: 'break-word',
  wordBreak: 'break-word'
});

const getToolbarOptions = ({ uploadCallback }) => ({
  options: ['inline', 'link', 'image'],
  inline: {
    inDropdown: false,
    options: ['bold', 'italic', 'underline'],
    bold: { className: 'react-draft-editor-icon' },
    italic: { className: 'react-draft-editor-icon' },
    underline: { className: 'react-draft-editor-icon' }
  },
  link: {
    inDropdown: false,
    options: ['link'],
    defaultTargetOption: '_blank',
    link: { className: 'react-draft-editor-icon' },
    unlink: { className: 'react-draft-editor-icon' }
  },
  image: {
    inDropdown: false,
    uploadCallback,
    previewImage: true,
    urlEnabled: false,
    uploadEnabled: true,
    alignmentEnabled: false,
    alt: { present: false },
    defaultSize: {
      height: 'auto',
      width: 'auto'
    },
    inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
    className: 'react-draft-editor-icon'
  },
  emoji: {
    inDropdown: false
  },
  history: {
    inDropdown: false,
    options: ['undo', 'redo']
  }
});

/*  Style overrides for react-draft-wysiwyg */
const StyleOverides = createGlobalStyle`
  .public-DraftStyleDefault-block {
    margin: 0.75em 0 1em !important;
    word-wrap: break-word !important;
    word-break: break-word !important;
    overflow-wrap: break-word !important;
    line-height: 120% !important;
  }
  .react-draft-editor-icon {
    background-color: var(--blue) !important;
  }

  .react-draft-editor-icon img {
    filter: brightness(0) invert(1) !important;
  }

  .react-draft-editor-icon:hover {
    box-shadow: 0.063rem 0.063rem 0 var(--dark-blue) !important;
  }

  .react-draft-editor-icon.rdw-option-active {
    background-color: var(--purple) !important;
    box-shadow: none !important;
    ${'' /* filter: brightness(114%); */}
  }

  .react-draft-editor-icon[title="Link"] {
    margin: 0 2px;

    @media (min-width: 390px) {
      margin: 0 4px;
    }
  }

  .react-draft-editor-icon[title="Image"] {
    margin: 0 2px;

    img {
      display: none;
    }

    &::before {
      content: '';
      display: block;
      width: 14px;
      height: 14px;
      margin-bottom: 0px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Crect x='1.5' y='2.5' width='17' height='15' rx='1.5' stroke='%23fff' stroke-width='2'/%3E%3Ccircle cx='6' cy='7' r='2' fill='%23fff'/%3E%3Cpath d='M1.5 14l4-5 4 4 3-4 5 6v2.5a1.5 1.5 0 0 1-1.5 1.5h-14a1.5 1.5 0 0 1-1.5-1.5V14z' fill='%23fff' stroke='%23fff' stroke-width='0.5'/%3E%3C/svg%3E");
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
    }

    @media (min-width: 390px) {
      margin: 0 4px;
    }
  }

  .rdw-link-modal {
    background-color: var(--blue) !important;
    border: 1px solid #F1F1F1 !important;
    box-shadow: 3px 3px 5px #BFBDBD !important;
    height: 176px !important;
  }

  .rdw-inline-wrapper,
  .rdw-link-wrapper,
  .rdw-emoji-wrapper {
    margin-bottom: 0 !important;
  }

  .rdw-link-modal-btn:not(:disabled) {
    background-color: #fff !important;
    border: 1px solid #F1F1F1 !important;
    color: #000 !important;
  }

  .rdw-link-modal-btn:disabled {
    cursor: not-allowed;
    background-color: #ece9e9 !important;
    border: 1px solid #F1F1F1 !important;
    color: rgba(000, 000, 000, 0.3) !important;
  }

  .rdw-link-modal-label {
    color: #fff !important;
  }

  /* Hides the 'open in seperate window?' box in the link modal */
  .rdw-link-modal-target-option {
    display: none !important;
  }

  .rdw-link-modal #linkTitle,
  .rdw-link-modal #linkTarget {
    color: var(---brown) !important;

    &:focus {
      background: #fff !important;
    }
  }

  /* Links */
  .rdw-link-decorator-wrapper a {
    text-decoration: underline !important;
    text-decoration-color: var(--purple) !important;

    span {
      color: var(--purple) !important;
    }
  }

  .rdw-link-decorator-icon {
    background-color: transparent !important;
    filter: invert(1);
  }

  /* Placeholder text */
  .public-DraftEditorPlaceholder-root {
    color: var(--light-brown) !important;
    font-family: 'Inter', sans-serif !important;
    font-size: 0.875rem !important;
    font-style: normal !important;
    font-weight: 400 !important;
    ${'' /* opacity: 0.65 !important; */}
  }

  .rdw-link-modal-buttonsection {
    margin-top: 9px !important;
  }

  /* Image modal styling */
  .rdw-image-modal-alt-input,
  .rdw-image-modal-alt-lbl,
  .rdw-image-modal-size,
  .rdw-image-modal-size-input,
  .rdw-image-modal-header {
    display: none !important;
  }

  .rdw-image-modal-upload-option-label {
    text-align: center !important;
  }

  .rdw-image-modal {
    background-color: var(--blue) !important;
    border: 1px solid #f1f1f1 !important;
    box-shadow: 3px 3px 5px #bfbdbd !important;
    width: 270px !important;
  }

  .rdw-image-modal-btn:not(:disabled) {
    background-color: #fff !important;
    border: 1px solid #f1f1f1 !important;
    color: #000 !important;
  }

  .rdw-image-modal-btn:disabled {
    cursor: not-allowed;
    background-color: #ece9e9 !important;
    border: 1px solid #f1f1f1 !important;
    color: rgba(000, 000, 000, 0.3) !important;
  }

  .rdw-image-modal-upload-option {
    background-color: #fff !important;
    border: 2px dashed var(--light-grey) !important;
    color: #000 !important;
  }

  .rdw-image-alignment-options-popup {
    display: none !important;
  }

  /* Force images to be left-aligned */
  .rdw-image-left,
  .rdw-image-center,
  .rdw-image-right,
  .rdw-image-imagewrapper {
    text-align: left !important;
    display: block !important;
    margin-left: 0 !important;
    margin-right: auto !important;
  }

  .markup img,
  .rdw-image-imagewrapper img {
    max-width: 100% !important;
    height: auto !important;
    display: block !important;
  }

  .react-draft-toolbar {
    background-color: transparent !important;
    border: transparent !important;
    border-radius: 18px !important;
    padding: 0 !important;
    position: absolute;
    bottom: 1px;
    left: 1px;
    margin-bottom: 0 !important;
    z-index: 3 !important;
  }

  .rdw-option-wrapper {
    cursor: pointer !important;
    height: 20px !important;
    width: 20px !important;
    border-radius: 50% !important;
    min-width: unset !important;
    padding: 2px !important;

    &[title="Bold"] img {
      width: 10px;
    }

    &[title="Italic"] img {
      margin-left: 1px;
      width: 15px;
    }

    &[title="Underline"] img {
      width: 15px;
      margin-top: 1px;
      margin-left: 1px;
    }

    &[title="Link"] img {
      width: 14px;
    }

    ${
      '' /* &[title="Emoji"] img {
      opacity: 0 !important;
    } */
    }
  }

  .rdw-dropdown-selectedtext {
    padding: 0 10px !important;
    ${'' /* z-index: 101 !important; */}

    img {
      filter: brightness(0) invert(1) !important;
    }
  }

  .rdw-inline-dropdown {
    background-color: var(--blue) !important;
    border-radius: var(--border-radius-lg) !important;
    height: 24px !important;
    width: 40px !important;
    position: relative !important;
    top: 3px !important;
  }

  .rdw-dropdown-carettoopen,
  .rdw-dropdown-carettoclose {
    right: 14% !important;
    filter: brightness(0) invert(1) !important;
    scale: 0.85 !important;
  }

  .rdw-dropdown-optionwrapper {
    overflow-y: auto !important;
    width: 86% !important;
    margin: 0 auto !important;
    border: none !important;
    border-bottom-left-radius: 12px !important;
    border-bottom-right-radius: 12px !important;
    top: -3px !important;

    padding-top: 3px !important;
    background-color: var(--blue) !important;
    position: relative !important;
    z-index: -1 !important;

    li[title="Bold"]:hover,
    li[title="Italic"]:hover,
    li[title="Underline"]:hover {
      background-color: var(--dark-blue) !important;
    }

    .rdw-dropdownoption-active {
      background-color: var(--purple) !important;
    }
  }

  .react-draft-wrapper {
    position: relative;
  }

  .react-draft-editor {
    font-family: 'Inter', sans-serif !important;
    font-weight: 400 !important;
    word-wrap: break-word !important;
    word-break: break-word !important;
    overflow-wrap: break-word !important;

    &::-webkit-scrollbar {
      display: none !important;
    }
  }

  .DraftEditor-editorContainer {
    z-index: 2 !important;
  }

  .rdw-suggestion-wrapper {
    ::-webkit-scrollbar {
      width: 0.55rem !important;
    }

    ::-webkit-scrollbar-track {
      box-shadow: inset 0 0 0.31rem grey !important;
      border-radius: 0.625rem !important;
    }

    ::-webkit-scrollbar-thumb {
      background-color: var(--blue) !important;
      border-radius: 0.625rem !important;
    }
  }

  .rdw-suggestion-dropdown {
    border: 1px solid var(--blue) !important;
    border-radius: 5px !important;
  }

  .rdw-suggestion-option {
    cursor: pointer;
    padding: 7px 12px 7px 5px !important;
    white-space: nowrap !important;

    &:hover {
      background-color: var(--light-blue) !important;
      color: var(--blue) !important;
    }
  }

  .rdw-mention-link {
    background-color: var(--light-blue) !important;
    color: var(--blue) !important;
    font-weight: bold;
  }

  .wysiwyg-mention {
    color: var(--blue);
    font-weight: bold;
    pointer-events: none !important;
    text-decoration: none;
  }

  .rdw-image-wrapper {
    margin-bottom: 0 !important;
  }

  /* Mobile responsive styles */
  @media (max-width: 519px) {
    .rdw-inline-wrapper,
    .rdw-link-wrapper,
    .rdw-image-wrapper {
      visibility: hidden !important;
      position: absolute !important;
      pointer-events: none !important;
    }

    /* Force modals to be visible and positioned correctly */
    .rdw-link-modal,
    .rdw-image-modal {
      visibility: visible !important;
      position: fixed !important;
      left: 50% !important;
      top: 50% !important;
      transform: translate(-50%, -50%) !important;
      pointer-events: auto !important;
      z-index: 1000 !important;
    }
  }
`;

function getSuggestions({ users, uid, sessionId }) {
  return users
    .filter((user) => user.id !== uid)
    .filter((user) => user.sessionsUserHasAccessTo.includes(sessionId))
    .map((user) => ({
      text: user.name,
      value: user.name,
      url: user.id
    }));
}

function EnterYourComment(props) {
  const {
    handlePostComment,
    handleEditComment,
    postingOrEditingComment,
    commentBeingEdited,
    userWantsToEditThisComment,
    userWantsToReplyToThisComment,
    setCommentBeingEdited,
    idOfCommentBeingRepliedTo,
    setIdOfCommentBeingRepliedTo,
    users,
    uid,
    parentComment,
    sessionId
  } = props;

  const t = useTranslations('Review');
  const tCommon = useTranslations('Common');
  const { language } = useLanguage();
  // react-draft-wysiwyg ships its own locale packs (incl. de/es/fr/pt/it) —
  // keyed by base language, so `pt-PT` becomes `pt`.
  const localization = {
    locale: language.split('-')[0],
    translations: {
      'components.controls.image.dropFileText': t('dropFile')
    }
  };
  const { firebase } = useContext(FirebaseContext);

  const uploadImageCallback = async (file) => {
    try {
      if (!firebase?.storageFactory) {
        throw new Error('Storage factory not initialized');
      }

      const downloadURL = await firebase.storageFactory.uploadQuestionImage(
        file,
        sessionId
      );

      return { data: { link: downloadURL } };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const htmlToDraftBlocks = (html) => {
    // eslint-disable-next-line global-require
    const htmlToDraft = require('html-to-draftjs').default; // Fixes this bug - https://github.com/jpuri/html-to-draftjs/issues/78
    const blocksFromHtml = htmlToDraft(html);
    const { contentBlocks, entityMap } = blocksFromHtml;
    const contentState = ContentState.createFromBlockArray(
      contentBlocks,
      entityMap
    );
    return EditorState.createWithContent(contentState);
  };

  const editorRef = useRef(null);

  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [editorIsFocused, setEditorIsFocused] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const { width } = useWindowSize();

  const currentContent = editorState?.getCurrentContent();
  const hasText = currentContent?.getPlainText().trim().length > 0;
  const hasAtomicBlocks = currentContent
    ?.getBlockMap()
    .some((block) => block.getType() === 'atomic');
  const editorContent = hasText || hasAtomicBlocks;

  useEffect(() => {
    // eslint-disable-next-line global-require
    Editor = require('react-draft-wysiwyg').Editor;
    setIsEditorReady(true);
  }, []);

  useEffect(() => {
    if (commentBeingEdited && userWantsToEditThisComment) {
      const _editorState = htmlToDraftBlocks(commentBeingEdited.html);
      setEditorState(_editorState);

      // Move cursor to end after browser has painted the visible editor
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setEditorState((currentState) =>
            EditorState.moveFocusToEnd(currentState)
          );
        });
      });
    }
  }, [commentBeingEdited, userWantsToEditThisComment]);

  useEffect(() => {
    if (userWantsToReplyToThisComment) {
      const _editorState = htmlToDraftBlocks(
        `<p><a href=${parentComment.uid} class="wysiwyg-mention" data-mention data-value="${parentComment.name}">@${parentComment.name}</a>&nbsp;</p>`
      );
      setEditorState(EditorState.moveFocusToEnd(_editorState));
    } else if (!userWantsToEditThisComment) {
      // Reset to empty state when not replying and not editing
      setEditorState(EditorState.createEmpty());
    }
  }, [
    userWantsToReplyToThisComment,
    userWantsToEditThisComment,
    parentComment
  ]);

  return (
    <Container
      $userWantsToEditThisComment={userWantsToEditThisComment}
      $userWantsToReplyToThisComment={userWantsToReplyToThisComment}>
      <StyleOverides editorIsFocused={editorIsFocused} />
      <EditorWrapper
        onFocus={() => setEditorIsFocused(true)}
        onBlur={() => {
          // if (commentBeingEdited) {
          //   setCommentBeingEdited(null);
          //   setEditorState(EditorState.createEmpty());
          // }
          setEditorIsFocused(false);
        }}
        $userWantsToReplyToThisComment={userWantsToReplyToThisComment}>
        {isEditorReady && (
          <>
            <Editor
              editorState={editorState}
              wrapperClassName="react-draft-wrapper"
              toolbarClassName="react-draft-toolbar"
              editorClassName="react-draft-editor"
              // wrapperStyle={wrapperStyle}
              toolbarStyle={toolbarStyle}
              editorStyle={editorStyle(editorIsFocused)}
              onEditorStateChange={setEditorState}
              toolbar={getToolbarOptions({
                width,
                uploadCallback: uploadImageCallback
              })}
              toolbarCustomButtons={
                width < 520
                  ? [
                      <ToolbarDropdown
                        className="toolbar-dropdown"
                        key="toolbar-dropdown"
                        editorState={editorState}
                        onChange={setEditorState}
                        size="small"
                      />
                    ]
                  : []
              }
              ref={editorRef}
              placeholder={
                userWantsToReplyToThisComment
                  ? t('replyPlaceholder')
                  : t('commentPlaceholder')
              }
              stripPastedStyles
              localization={localization}
              mention={{
                separator: ' ',
                trigger: '@',
                suggestions: getSuggestions({ users, uid, sessionId })
              }}
            />
            <BottomPadding editorIsFocused={editorIsFocused} />
            <ButtonRow>
              {(userWantsToReplyToThisComment ||
                userWantsToEditThisComment) && (
                <Button
                  className="btn cancel-btn"
                  backgroundColor="var(--red)"
                  textColor="#fff"
                  onClick={async () => {
                    if (userWantsToEditThisComment) {
                      setCommentBeingEdited(null);
                    } else if (userWantsToReplyToThisComment) {
                      setIdOfCommentBeingRepliedTo(null);
                    }
                    // setEditorState(EditorState.createEmpty());
                  }}>
                  {tCommon('cancel')}
                </Button>
              )}
              <Button
                disabled={!editorContent}
                loading={postingOrEditingComment}
                className={
                  userWantsToEditThisComment
                    ? 'btn update-btn'
                    : 'btn comment-btn'
                }
                onClick={async () => {
                  if (editorContent) {
                    if (userWantsToEditThisComment) {
                      await handleEditComment({
                        editorState,
                        commentBeingEdited,
                        parentCommentId: idOfCommentBeingRepliedTo,
                        parentCommenterUID: parentComment.uid
                      });
                      setEditorState(EditorState.createEmpty());
                      setCommentBeingEdited(null);
                    } else if (userWantsToReplyToThisComment) {
                      await handlePostComment({
                        editorState,
                        parentCommentId: idOfCommentBeingRepliedTo,
                        parentCommenterUID: parentComment.uid,
                        parentCommenterName: parentComment.name
                      });
                      setIdOfCommentBeingRepliedTo(null);
                      setEditorState(EditorState.createEmpty());
                    } else {
                      await handlePostComment({ editorState });
                      setEditorState(EditorState.createEmpty());
                    }
                  }
                }}>
                {userWantsToEditThisComment ? tCommon('update') : t('comment')}
              </Button>
            </ButtonRow>
          </>
        )}
      </EditorWrapper>
    </Container>
  );
}

const ButtonRow = styled.div`
  bottom: 6px;
  display: flex;
  gap: 8px;
  position: absolute;
  right: 7px;
  z-index: 2;
`;

const BottomPadding = styled.div`
  background-color: ${({ editorIsFocused }) =>
    editorIsFocused ? '#f2f4f7' : '#fff'};
  border-bottom-left-radius: var(--border-radius-md);
  border-bottom-right-radius: var(--border-radius-md);
  bottom: 1px;
  height: 31px;
  left: 8px;
  position: absolute;
  width: calc(100% - 15px);
  z-index: 2;
`;

const Container = styled(motion.div)`
  background-color: #fff;
  position: relative;
  width: 100%;

  ${({ $userWantsToEditThisComment, $userWantsToReplyToThisComment }) =>
    !$userWantsToEditThisComment &&
    !$userWantsToReplyToThisComment &&
    css`
      z-index: 0 !important;
    `};

  ${({ $userWantsToEditThisComment, $userWantsToReplyToThisComment }) =>
    $userWantsToEditThisComment
      ? css`
          padding: 0 0 6px 0;
        `
      : css`
          border-radius: var(--border-radius-md);
          box-shadow: ${$userWantsToReplyToThisComment
            ? 'none'
            : '0px -3px 6px 0px rgba(226, 228, 234, 0.4)'};
          padding: ${$userWantsToReplyToThisComment ? '6px 0' : '9px'};
          top: ${$userWantsToReplyToThisComment ? 0 : '-7px'};
        `}
`;

const EditorWrapper = styled.div`
  position: relative;

  .toolbar-dropdown {
    bottom: 5px !important;
    left: 5px !important;
  }

  /* Buttons size to their content — the old per-button min-width and right
     offsets were tuned to the English labels and overlapped once translated
     labels grew (§9.5) */
  .btn {
    font-size: 14px;
    font-weight: 500;
    padding: 0.29em 1.2em;
  }

  .loading-spinner {
    height: 14px;
    width: 14px;
  }

  .rdw-link-modal {
    left: -50px !important;

    @media (min-width: 457px) {
      left: 5px !important;
    }
  }
`;

export default EnterYourComment;
